import { useState, useEffect, useContext } from 'react'
import { motion } from 'framer-motion'
import { formatPrice, PRICING_PLANS } from '../config/constants'
import { createPaymentOrder, verifyPayment } from '../services/paymentService'
import { handleApiError } from '../utils/errorHandler'
import { devLog, devError } from '../utils/devLog'
import { ToastContext } from '../App'
import Button from './ui/Button'

export default function PaymentFlow({ submissionId, planId, videoHours, calculatedPrice, onPaymentSuccess, onBack }) {
  const toast = useContext(ToastContext)
  const [paymentLoading, setPaymentLoading] = useState(false)
  const [razorpayLoaded, setRazorpayLoaded] = useState(false)

  const handleBack = () => {
    if (window.confirm('Your submission is saved. Leave without paying? You can return to this page and pay later using the same link.')) {
      onBack()
    }
  }

  const plan = PRICING_PLANS.find(p => p.id === planId)

  // Load Razorpay script
  useEffect(() => {
    // Check if already loaded
    if (window.Razorpay && typeof window.Razorpay === 'function') {
      setRazorpayLoaded(true)
      devLog('Razorpay already loaded')
      return
    }

    const script = document.createElement('script')
    script.src = 'https://checkout.razorpay.com/v1/checkout.js'
    script.async = true
    script.onload = () => {
      // Small delay to ensure Razorpay is fully initialized
      setTimeout(() => {
        if (window.Razorpay && typeof window.Razorpay === 'function') {
          setRazorpayLoaded(true)
          devLog('Razorpay script loaded and initialized')
        } else {
          devError('Razorpay not available after script load')
          toast?.error('Failed to initialize payment gateway. Please refresh the page.')
        }
      }, 100)
    }
    script.onerror = () => {
      devError('Failed to load Razorpay script')
      toast?.error('Failed to load payment gateway. Please refresh the page.')
    }
    document.body.appendChild(script)

    return () => {
      // Only remove if we added it
      if (script.parentNode) {
        document.body.removeChild(script)
      }
    }
  }, [toast])

  const handlePayment = async () => {
    if (!calculatedPrice || calculatedPrice <= 0) {
      toast?.error('Please calculate a valid price')
      return
    }

    // Check if Razorpay is loaded
    if (!razorpayLoaded || !window.Razorpay) {
      toast?.error('Payment gateway is still loading. Please wait a moment and try again.')
      return
    }

    setPaymentLoading(true)

    try {
      devLog('Creating payment order', { submissionId, planId, videoHours, amount: calculatedPrice })
      
      // Create payment order on backend
      const orderResult = await createPaymentOrder({
        submissionId,
        planId,
        videoHours,
        amount: calculatedPrice,
      })

      devLog('Order result', orderResult)

      if (!orderResult.success) {
        const errorMsg = orderResult.error || 'Failed to create payment order'
        devError('Order creation failed', errorMsg)
        throw new Error(errorMsg)
      }

      const { orderId, razorpayOrderId } = orderResult.data

      if (!razorpayOrderId) {
        throw new Error('Razorpay order ID not received from server')
      }

      // Get Razorpay key from environment
      const razorpayKey = import.meta.env.VITE_RAZORPAY_KEY_ID
      
      if (!razorpayKey) {
        throw new Error('Razorpay key not configured. Please set VITE_RAZORPAY_KEY_ID in .env file')
      }

      devLog('Initializing Razorpay checkout', { razorpayOrderId, amount: calculatedPrice })
      
      // Razorpay Checkout options - using order_id (OFFICIAL Razorpay format)
      // According to Razorpay docs: When using order_id, DO NOT pass amount/currency
      // Required: key, order_id, name, handler
      // Optional: description, theme, modal
      const options = {
        key: razorpayKey, // Required: API Key ID
        order_id: razorpayOrderId, // Required: Order ID from backend (contains amount & currency)
        name: 'Reel Cricket', // Required: Business name
        description: `${plan?.name} - ${videoHours} hour(s) video editing`, // Optional: Description
        handler: async (response) => {
          devLog('Payment successful callback', response)
          setPaymentLoading(true) // Keep loading during verification
          
          // Verify payment on backend
          try {
            const verifyResult = await verifyPayment({
              orderId,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpayOrderId: response.razorpay_order_id,
              razorpaySignature: response.razorpay_signature,
            })

            devLog('Verification result', verifyResult)

            if (verifyResult.success) {
              toast?.success('Payment successful!')
              const orderId = verifyResult.data?.orderId
              onPaymentSuccess(orderId)
            } else {
              toast?.error(verifyResult.error || 'Payment verification failed. You can try Pay again.')
              setPaymentLoading(false)
            }
          } catch (error) {
            devError('Payment verification error', error)
            toast?.error('Verification failed. Please contact support with payment ID: ' + response.razorpay_payment_id + '. You can try Pay again.')
            setPaymentLoading(false)
          }
        },
        theme: {
          color: '#00ff88', // Optional: Brand color
        },
        modal: {
          ondismiss: () => {
            devLog('Payment modal dismissed')
            setPaymentLoading(false)
          },
        },
        // Note: retry.max_count is NOT supported in Web Integration (only Android/iOS)
        // So we only enable retry without max_count
        retry: {
          enabled: true, // Enable retry (max_count not supported in web)
        },
      }
      
      // Verify Razorpay is properly loaded and ready
      if (!window.Razorpay || typeof window.Razorpay !== 'function') {
        throw new Error('Razorpay SDK not loaded. Please refresh the page.')
      }

      // Final validation: Ensure order_id is valid format
      if (!razorpayOrderId || !razorpayOrderId.startsWith('order_') || razorpayOrderId.length < 10) {
        throw new Error('Invalid Razorpay order ID format. Please try again.')
      }

      // Log configuration for debugging (verify no amount/currency)
      const configCheck = {
        key: razorpayKey.substring(0, 15) + '...',
        order_id: razorpayOrderId,
        hasAmount: 'amount' in options ? '❌ YES (WRONG!)' : '✅ NO (CORRECT)',
        hasCurrency: 'currency' in options ? '❌ YES (WRONG!)' : '✅ NO (CORRECT)',
        optionsKeys: Object.keys(options).join(', '),
        retryConfig: options.retry,
      }
      devLog('Razorpay checkout configuration', configCheck)
      
      // Final validation: Ensure we're not passing conflicting parameters
      if ('amount' in options || 'currency' in options) {
        throw new Error('CRITICAL: Cannot pass amount/currency when using order_id. This will cause Razorpay errors.')
      }

      // Create Razorpay instance with minimal, correct options
      // This ensures Razorpay uses the order_id flow, not the internal AJAX endpoint
      const razorpay = new window.Razorpay(options)
      
      // Verify instance was created successfully
      if (!razorpay || typeof razorpay.open !== 'function') {
        throw new Error('Failed to initialize Razorpay checkout. Please refresh and try again.')
      }

      devLog('Razorpay checkout initialized. Opening payment modal')

      razorpay.on('payment.failed', (response) => {
        devError('Payment failed', response)
        const errorCode = response.error?.code
        const errorDesc = response.error?.description || response.error?.reason || 'Unknown error'
        
        // Provide specific guidance based on error
        let userMessage = errorDesc
        if (errorCode === 'SERVER_ERROR') {
          userMessage = 'Razorpay server error. This usually means your Razorpay account needs activation. Please check your Razorpay dashboard or contact Razorpay support.'
        } else if (errorDesc.includes('International cards')) {
          userMessage = 'International cards are disabled. Enable them in Razorpay Dashboard → Settings → Payment Methods → International Cards'
        }
        
        toast?.error(`Payment failed: ${userMessage}`)
        setPaymentLoading(false)
      })

      razorpay.on('payment.authorized', (response) => {
        devLog('Payment authorized', response)
      })

      razorpay.open()
    } catch (error) {
      devError('Payment error', error)
      const errorMessage = handleApiError(error)
      toast?.error(errorMessage)
      setPaymentLoading(false)
    }
  }

  return (
    <div className="pt-24 pb-20 px-4 min-h-screen">
      <div className="container mx-auto max-w-2xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-stadium-black to-gray-900 border-2 border-pitch-green/20 rounded-2xl p-8 md:p-12"
        >
          <h2 className="text-3xl md:text-4xl font-black mb-2 text-center">
            <span className="text-white">COMPLETE </span>
            <span className="text-pitch-green text-glow">PAYMENT</span>
          </h2>
          <p className="text-gray-400 text-center mb-8">
            Secure payment powered by Razorpay
          </p>

          {/* Order Summary */}
          <div className="bg-gray-900/50 rounded-xl p-6 mb-6 border border-pitch-green/10">
            <h3 className="text-pitch-green font-bold text-lg mb-4">Order Summary</h3>
            
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-400">Package:</span>
                <span className="text-white font-semibold">{plan?.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Rate:</span>
                <span className="text-white">{formatPrice(plan?.pricePerHour || 0)}/hour</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Video Duration:</span>
                <span className="text-white">{videoHours} hour(s)</span>
              </div>
              <div className="border-t border-gray-700 pt-3 mt-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-300 font-semibold text-lg">Total Amount:</span>
                  <span className="text-pitch-green text-3xl font-black">
                    {formatPrice(calculatedPrice)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Button */}
          <div className="space-y-4">
            {!razorpayLoaded && (
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 mb-4">
                <p className="text-yellow-400 text-sm text-center">
                  ⏳ Loading payment gateway...
                </p>
              </div>
            )}
            <Button
              onClick={handlePayment}
              disabled={paymentLoading || !calculatedPrice || calculatedPrice <= 0 || !razorpayLoaded}
              loading={paymentLoading}
              size="lg"
              className="w-full"
            >
              {paymentLoading ? 'Processing...' : !razorpayLoaded ? 'Loading...' : `Pay ${formatPrice(calculatedPrice)}`}
            </Button>

            <Button
              onClick={handleBack}
              variant="outline"
              className="w-full"
              disabled={paymentLoading}
            >
              Back to Form
            </Button>
          </div>

          <p className="text-gray-500 text-xs text-center mt-6">
            🔒 Your payment is secured by Razorpay. We never store your card details.
          </p>
        </motion.div>
      </div>
    </div>
  )
}
